import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Edit3, Check } from 'lucide-react';

const Step5About = ({ displayName, bio, onUpdate, onNext, onBack }) => {
  const [inputDisplayName, setInputDisplayName] = useState(displayName);
  const [inputBio, setInputBio] = useState(bio);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);

  useEffect(() => {
    setInputDisplayName(displayName);
    setInputBio(bio);
  }, [displayName, bio]);

  const handleContinue = () => {
    if (inputDisplayName.trim() && inputBio.trim()) onNext();
  };

  const handleDisplayNameSave = () => {
    if (inputDisplayName.trim()) {
      onUpdate({ displayName: inputDisplayName.trim() });
      setIsEditingName(false);
    }
  };

  const handleBioSave = () => {
    onUpdate({ bio: inputBio.trim() });
    setIsEditingBio(false);
  };

  const canContinue = inputDisplayName.trim() && inputBio.trim();

  return (
    <StepContainer>
      <StepCard>
        <StepHeader>
          <StepTitle>About you</StepTitle>
          <StepSubtitle>Tell your supporters about yourself and what you do</StepSubtitle>
        </StepHeader>

        <FormContainer>
          <FormSection>
            <SectionHeader>
              <SectionTitle>Display name</SectionTitle>
              {!isEditingName ? (
                <EditButton onClick={() => setIsEditingName(true)}><Edit3 size={16} /> Edit</EditButton>
              ) : (
                <SaveButton onClick={handleDisplayNameSave}><Check size={16} /> Save</SaveButton>
              )}
            </SectionHeader>
            {isEditingName ? (
              <InputField
                type="text"
                value={inputDisplayName}
                onChange={(e) => setInputDisplayName(e.target.value)}
                placeholder="Enter your display name"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleDisplayNameSave()}
              />
            ) : (
              <DisplayValue>{inputDisplayName || 'No display name set'}</DisplayValue>
            )}
          </FormSection>

          <FormSection>
            <SectionHeader>
              <SectionTitle>Bio</SectionTitle>
              {!isEditingBio ? (
                <EditButton onClick={() => setIsEditingBio(true)}><Edit3 size={16} /> Edit</EditButton>
              ) : (
                <SaveButton onClick={handleBioSave}><Check size={16} /> Save</SaveButton>
              )}
            </SectionHeader>
            {isEditingBio ? (
              <TextAreaField
                value={inputBio}
                onChange={(e) => setInputBio(e.target.value)}
                placeholder="Tell your story, what you create, and why supporters should follow you..."
                rows={4}
                autoFocus
              />
            ) : (
              <DisplayValue>{inputBio || 'No bio set'}</DisplayValue>
            )}
          </FormSection>
        </FormContainer>

        <PreviewContainer>
          <PreviewTitle>Preview:</PreviewTitle>
          <ProfilePreview>
            <PreviewHeader>
              <PreviewAvatar><User size={32} /></PreviewAvatar>
              <PreviewInfo>
                <PreviewName>{inputDisplayName || 'Your Display Name'}</PreviewName>
                <PreviewUrl>bucx.io/username</PreviewUrl>
              </PreviewInfo>
            </PreviewHeader>
            <PreviewBio>{inputBio || 'Your bio will appear here...'}</PreviewBio>
            <PreviewStats>
              <StatItem>
                <StatNumber>0</StatNumber>
                <StatLabel>Supporters</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>0</StatNumber>
                <StatLabel>Posts</StatLabel>
              </StatItem>
            </PreviewStats>
          </ProfilePreview>
        </PreviewContainer>

        <TipContainer>
          <TipIcon>💡</TipIcon>
          <TipText>Keep your bio engaging and authentic. This is what supporters will see first!</TipText>
        </TipContainer>

        <ButtonContainer>
          <BackButton onClick={onBack} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ArrowLeft size={20} /> Back
          </BackButton>
          <NextButton
            onClick={handleContinue}
            disabled={!canContinue}
            $disabled={!canContinue}
            whileHover={{ scale: canContinue ? 1.05 : 1 }}
            whileTap={{ scale: canContinue ? 0.95 : 1 }}
          >
            Next
          </NextButton>
        </ButtonContainer>
      </StepCard>
    </StepContainer>
  );
};

const StepContainer = styled.div`width: 100%; max-width: 700px; display: flex; justify-content: center;`;
const StepCard = styled.div`background: #111111; border: 1px solid #333333; border-radius: 20px; padding: 3rem; width: 100%; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);`;
const StepHeader = styled.div`text-align: center; margin-bottom: 3rem;`;
const StepTitle = styled.h2`font-size: 2.5rem; font-weight: bold; color: #ffffff; margin: 0 0 1rem 0; font-family: var(--font-heading);`;
const StepSubtitle = styled.p`font-size: 1.1rem; color: #cccccc; margin: 0;`;
const FormContainer = styled.div`margin-bottom: 2rem;`;
const FormSection = styled.div`margin-bottom: 2rem;`;
const SectionHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;`;
const SectionTitle = styled.h3`font-size: 1.1rem; font-weight: 600; color: #ffffff; margin: 0;`;
const EditButton = styled.button`display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 1rem; background: #1a1a1a; color: #cccccc; border: 1px solid #333333; border-radius: 8px; cursor: pointer; transition: all 0.3s; &:hover { border-color: #e0c16f; color: #e0c16f; }`;
const SaveButton = styled.button`display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 1rem; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;`;
const InputField = styled.input`width: 100%; padding: 1rem; border: 2px solid #333333; border-radius: 12px; font-size: 1rem; color: #ffffff; background: #1a1a1a; transition: all 0.3s; &:focus { outline: none; border-color: #e0c16f; }`;
const TextAreaField = styled.textarea`width: 100%; padding: 1rem; border: 2px solid #333333; border-radius: 12px; font-size: 1rem; color: #ffffff; background: #1a1a1a; resize: vertical; min-height: 120px; transition: all 0.3s; &:focus { outline: none; border-color: #e0c16f; }`;
const DisplayValue = styled.div`padding: 1rem; background: #1a1a1a; border: 2px solid #333333; border-radius: 12px; font-size: 1rem; color: #cccccc; min-height: 60px; display: flex; align-items: center;`;
const PreviewContainer = styled.div`margin-bottom: 2rem;`;
const PreviewTitle = styled.h3`font-size: 1.1rem; color: #ffffff; margin: 0 0 1rem 0;`;
const ProfilePreview = styled.div`border: 2px solid #333333; border-radius: 15px; padding: 1.5rem; background: #1a1a1a;`;
const PreviewHeader = styled.div`display: flex; align-items: center; margin-bottom: 1rem;`;
const PreviewAvatar = styled.div`width: 50px; height: 50px; border-radius: 50%; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); display: flex; align-items: center; justify-content: center; color: #000000; margin-right: 1rem;`;
const PreviewInfo = styled.div`flex: 1;`;
const PreviewName = styled.h4`font-size: 1.2rem; color: #ffffff; margin: 0 0 0.25rem 0;`;
const PreviewUrl = styled.p`font-size: 0.9rem; color: #e0c16f; margin: 0;`;
const PreviewBio = styled.p`font-size: 0.9rem; color: #cccccc; margin: 0 0 1rem 0;`;
const PreviewStats = styled.div`display: flex; gap: 2rem;`;
const StatItem = styled.div`text-align: center;`;
const StatNumber = styled.div`font-size: 1.5rem; font-weight: bold; color: #e0c16f;`;
const StatLabel = styled.div`font-size: 0.8rem; color: #888; text-transform: uppercase;`;
const TipContainer = styled.div`display: flex; align-items: center; gap: 0.5rem; padding: 1rem; background: rgba(224, 193, 111, 0.1); border: 1px solid rgba(224, 193, 111, 0.2); border-radius: 8px; margin-bottom: 2rem;`;
const TipIcon = styled.span`font-size: 1.2rem;`;
const TipText = styled.p`font-size: 0.9rem; color: #e0c16f; margin: 0;`;
const ButtonContainer = styled.div`display: flex; gap: 1rem;`;
const BackButton = styled(motion.button)`display: flex; align-items: center; gap: 0.5rem; padding: 1rem 2rem; background: #1a1a1a; color: #cccccc; border: 2px solid #333333; border-radius: 12px; cursor: pointer; transition: all 0.3s; &:hover { background: #2a2a2a; }`;
const NextButton = styled(motion.button)`flex: 1; padding: 1rem 2rem; background: ${p => p.$disabled ? '#333333' : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'}; color: ${p => p.$disabled ? '#666666' : '#000000'}; border: none; border-radius: 12px; font-weight: 600; font-size: 1rem; cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};`;

export default Step5About;